import axios from 'axios';
import DOMPurify from 'dompurify';

// Django mounts the REST API under /api/. Production env may already include /api; localhost must not omit it.
const _apiOrigin = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
export const API_BASE_URL = _apiOrigin.endsWith('/api') ? _apiOrigin : `${_apiOrigin}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Multi-site: allow callers to set the current site; stored in localStorage
// and automatically attached to every request via the interceptor below.
let _currentSiteId = (() => {
  const stored = localStorage.getItem('current_site_id');
  return stored ? Number(stored) : null;
})();

export function setCurrentSiteId(siteId) {
  _currentSiteId = siteId;
  if (siteId) {
    localStorage.setItem('current_site_id', String(siteId));
  } else {
    localStorage.removeItem('current_site_id');
  }
}

export function getCurrentSiteId() {
  return _currentSiteId;
}

// Request interceptor to add auth token + X-Site-Id header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (_currentSiteId) {
      config.headers['X-Site-Id'] = String(_currentSiteId);
    }
    // Let the browser set multipart boundary (default application/json breaks file uploads).
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      const msg =
        retryAfter != null && retryAfter !== ''
          ? `Rate limit reached. Try again in ${retryAfter} seconds.`
          : 'You have sent too many requests. Please wait a few minutes and try again.';
      const wrapped = new Error(msg);
      wrapped.response = error.response;
      wrapped.isRateLimit = true;
      return Promise.reject(wrapped);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        localStorage.setItem('access_token', access);
        // ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION: the old refresh is
        // blacklisted on this call. Persist the new one or the next 401 logs the user out.
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }
        originalRequest.headers.Authorization = `Bearer ${access}`;

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Sanitize input to prevent XSS
export const sanitizeInput = (input) => {
  if (typeof input === 'string') {
    return DOMPurify.sanitize(input);
  }
  return input;
};

// API methods
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/token/', {
      email: sanitizeInput(email),
      password,
    });
    if (response.data.access) {
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
    }
    return response.data;
  },
  logout: () => {
    const refresh = localStorage.getItem('refresh_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    if (refresh) {
      axios.post(`${API_BASE_URL}/auth/token/blacklist/`, { refresh }).catch(() => {});
    }
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/users/me/');
    return response.data;
  },
};

export const sessionAPI = {
  getSessions: (params = {}) => api.get('/sessions/sessions/', { params }),
  getSites: () => api.get('/sessions/sites/'),
  getSession: (id) => api.get(`/sessions/sessions/${id}/`),
  updateSession: (id, data) => api.patch(`/sessions/sessions/${id}/`, data),
  getCurrentSession: () => api.get('/sessions/current-session/'),
  createSession: (data) => api.post('/sessions/sessions/', data),
  setActiveSession: (sessionId) =>
    api.post(`/sessions/sessions/${sessionId}/set_active/`),
  copySession: (targetId, sourceId) =>
    api.post(`/sessions/sessions/${targetId}/copy/`, {
      source_session_id: sourceId,
    }),
  getCopyStudentsPreview: (targetSessionId, sourceSessionId) =>
    api.get(`/sessions/sessions/${targetSessionId}/copy-students-preview/`, {
      params: { source: sourceSessionId },
    }),
  copyStudentsFromSession: (targetSessionId, sourceSessionId) =>
    api.post(`/sessions/sessions/${targetSessionId}/copy-students/`, {
      source: sourceSessionId,
    }),
  getOffDays: (sessionId) =>
    api.get('/sessions/off-days/', { params: sessionId ? { session: sessionId } : {} }),
  createOffDay: (data) => api.post('/sessions/off-days/', data),
  updateOffDay: (id, data) => api.patch(`/sessions/off-days/${id}/`, data),
  deleteOffDay: (id) => api.delete(`/sessions/off-days/${id}/`),
  getDailySchedules: (sessionId) =>
    api.get('/sessions/daily-schedules/', { params: sessionId ? { session: sessionId } : {} }),
  createDailySchedule: (data) => api.post('/sessions/daily-schedules/', data),
  updateDailySchedule: (id, data) => api.patch(`/sessions/daily-schedules/${id}/`, data),
  deleteDailySchedule: (id) => api.delete(`/sessions/daily-schedules/${id}/`),
  getJewishHolidays: (start, end) =>
    api.get('/sessions/jewish-holidays/', { params: { start, end } }),
  getClassrooms: (sessionId) =>
    api.get('/sessions/classrooms/', {
      params: sessionId ? { session: sessionId } : {},
    }),
  getFundingCodes: (sessionId) =>
    api.get('/sessions/funding-codes/', {
      params: sessionId ? { session: sessionId } : {},
    }),
  getSchoolDistricts: (sessionId) =>
    api.get('/sessions/school-districts/', {
      params: sessionId ? { session: sessionId } : {},
    }),
  getRoster: (sessionId) =>
    api.get('/sessions/roster/', {
      params: sessionId ? { session: sessionId } : {},
    }),
  getMedicalDueReport: (params) =>
    api.get('/sessions/medical-due-report/', { params }),
  getMedicalDueReportCsv: (params) =>
    api.get('/sessions/medical-due-report/', { params: { ...params, export: 'csv' }, responseType: 'blob' }),
  getIncidents: (params) =>
    api.get('/sessions/incidents/', { params }),
  getIncident: (id) => api.get(`/sessions/incidents/${id}/`),
  createIncident: (data) => api.post('/sessions/incidents/', data),
  updateIncident: (id, data) => api.patch(`/sessions/incidents/${id}/`, data),
  deleteIncident: (id) => api.delete(`/sessions/incidents/${id}/`),
  getAbsenceReasons: () => api.get('/sessions/absence-reasons/'),
  getAttendance: (params) => api.get('/sessions/attendance/', { params }),
  getAttendanceDailyAbsentReport: (params) =>
    api.get('/sessions/attendance/daily-absent-report/', { params }),
  getAttendanceDailyAbsentReportCsv: (params) =>
    api.get('/sessions/attendance/daily-absent-report/', { params: { ...params, export: 'csv' }, responseType: 'blob' }),
  getMonthlyAttendanceLogPdf: (params) =>
    api.get('/sessions/attendance/monthly-attendance-log/', { params, responseType: 'blob' }),
  createAttendanceRecord: (data) => api.post('/sessions/attendance/', data),
  updateAttendanceRecord: (id, data) => api.patch(`/sessions/attendance/${id}/`, data),
  /** Batch upsert for nurse per-class workflow (IsAdminOrEditor). */
  bulkSetAttendance: (data) => api.post('/sessions/attendance/bulk-set/', data),
  getCpseEnrollments: (params) => api.get('/sessions/cpse-enrollments/', { params }),
  createCpseEnrollment: (data) => api.post('/sessions/cpse-enrollments/', data),
  updateCpseEnrollment: (id, data) => api.patch(`/sessions/cpse-enrollments/${id}/`, data),
  deleteCpseEnrollment: (id) => api.delete(`/sessions/cpse-enrollments/${id}/`),
  importCpseEnrollments: (sessionId, file) => {
    const form = new FormData();
    form.append('file', file);
    if (sessionId) form.append('session', sessionId);
    return api.post('/sessions/cpse/enrollments/import/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getCpseAttendanceExportReport: (params) =>
    api.get('/sessions/cpse/attendance-export/', { params: { ...params, report: '1' } }),
  getCpseAttendanceExportXml: (params) =>
    api.get('/sessions/cpse/attendance-export/', { params, responseType: 'blob' }),
  getCpseAideWeek: (params) => api.get('/sessions/cpse/aide-assignments/', { params }),
  bulkSetCpseAideAssignments: (data) => api.post('/sessions/cpse-aide-assignments/bulk-set/', data),
  getCpseAideLogXlsx: (params) =>
    api.get('/sessions/cpse/aide-log/', { params, responseType: 'blob' }),
};

export const studentAPI = {
  getStudents: (sessionId, params = {}) =>
    api.get('/sessions/students/', {
      params: {
        ...(sessionId ? { session: sessionId } : {}),
        page_size: 2000,
        ...params,
      },
    }),
  downloadRosterCsv: async (sessionId, params = {}) => {
    const res = await api.get('/sessions/students/', {
      params: { session: sessionId || '', page_size: 5000, export: 'csv', ...params },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_roster.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
  getStudent: (id) => api.get(`/sessions/students/${id}/`),
  createStudent: (data) => api.post('/sessions/students/', sanitizeInput(data)),
  updateStudent: (id, data) =>
    api.patch(`/sessions/students/${id}/`, sanitizeInput(data)),
  // Hard delete is blocked server-side (405) — archive/restore instead (admin/superadmin).
  archiveStudent: (id) => api.post(`/sessions/students/${id}/archive/`),
  restoreStudent: (id) => api.post(`/sessions/students/${id}/restore/`),
  uploadPdf: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/sessions/students/${id}/upload-pdf/`, formData);
  },
  deletePdf: (id) => api.delete(`/sessions/students/${id}/uploaded-pdf/`),
  openPdf: async (id) => {
    const res = await api.get(`/sessions/students/${id}/pdf/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  // Legal/audit documents (NYS special ed)
  getDocuments: (studentId, params = {}) =>
    api.get(`/sessions/students/${studentId}/documents/`, { params: { page_size: 500, ...params } }),
  uploadDocument: (studentId, file, category, title = '', options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (title) formData.append('title', title);
    if (options.description != null) formData.append('description', options.description);
    if (options.date_of_document) formData.append('date_of_document', options.date_of_document);
    if (category === 'other_custom' && options.custom_category) formData.append('custom_category', options.custom_category);
    return api.post(`/sessions/students/${studentId}/documents/`, formData);
  },
  deleteDocument: (studentId, docId) =>
    api.delete(`/sessions/students/${studentId}/documents/${docId}/`),
  downloadDocument: async (studentId, docId, filename) => {
    const res = await api.get(`/sessions/students/${studentId}/documents/${docId}/download/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    a.click();
    URL.revokeObjectURL(url);
  },
  /** Blob object URL for iframe preview (caller must revoke). */
  getDocumentObjectUrl: async (studentId, docId) => {
    const res = await api.get(`/sessions/students/${studentId}/documents/${docId}/download/`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },
  /** OCR medical suggestions — does not persist profile. */
  ocrMedicalSuggestions: (studentId, docId) =>
    api.post(`/sessions/students/${studentId}/documents/${docId}/ocr-medical-suggestions/`),
  downloadDocumentPackage: async (studentId, documentIds) => {
    const res = await api.post(`/sessions/students/${studentId}/documents/package/`, { document_ids: documentIds }, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_${studentId}_docs.zip`;
    a.click();
    URL.revokeObjectURL(url);
  },
  printToPdf: async (studentId, documentIds) => {
    const res = await api.post(`/sessions/students/${studentId}/documents/print-pdf/`, { document_ids: documentIds }, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_${studentId}_documents.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  exitPackageCheck: (studentId) => api.get(`/sessions/students/${studentId}/exit-package/check/`),
  exitPackageGenerate: async (studentId) => {
    const res = await api.post(`/sessions/students/${studentId}/exit-package/generate/`, {}, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exit_package_${studentId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  exitPackageNotify: (studentId, userId, channel) =>
    api.post(`/sessions/students/${studentId}/exit-package/notify/`, { user_id: userId, channel }),
  // Parent/Guardian (v2.2.0 Phase 2)
  getParentGuardian: (studentId) => api.get(`/sessions/students/${studentId}/parent-guardian/`),
  updateParentGuardian: (studentId, data) =>
    api.patch(`/sessions/students/${studentId}/parent-guardian/`, sanitizeInput(data)),
  getEmergencyContacts: (studentId) =>
    api.get(`/sessions/students/${studentId}/parent-guardian/emergency-contacts/`),
  createEmergencyContact: (studentId, data) =>
    api.post(`/sessions/students/${studentId}/parent-guardian/emergency-contacts/`, sanitizeInput(data)),
  updateEmergencyContact: (studentId, contactId, data) =>
    api.patch(`/sessions/students/${studentId}/parent-guardian/emergency-contacts/${contactId}/`, sanitizeInput(data)),
  deleteEmergencyContact: (studentId, contactId) =>
    api.delete(`/sessions/students/${studentId}/parent-guardian/emergency-contacts/${contactId}/`),
  // Parent Packet (Phase 3 / 4.2)
  getParentPacketForms: (studentId) => api.get(`/sessions/students/${studentId}/parent-packet/forms/`),
  getParentPacketFormPdf: (studentId, formId) =>
    api.get(`/sessions/students/${studentId}/parent-packet/forms/${formId}/pdf/`, { responseType: 'blob' }),
  getParentPacketPreview: (studentId) =>
    api.get(`/sessions/students/${studentId}/parent-packet/preview/`, { responseType: 'blob' }),
  sendParentPacket: (studentId) => api.post(`/students/${studentId}/parent-packet/send/`),
  getParentPacketStatus: (studentId) => api.get(`/students/${studentId}/parent-packet/status/`),
  remindParentPacket: (studentId) => api.post(`/students/${studentId}/parent-packet/remind/`),
  resetParentPacket: (studentId) => api.post(`/students/${studentId}/parent-packet/reset/`),
  getStudentSocialHistory: (studentId) => api.get(`/sessions/students/${studentId}/social-history/`),
  updateStudentSocialHistory: (studentId, data) => api.patch(`/sessions/students/${studentId}/social-history/`, data),
  getStudentFeedingQuestionnaire: (studentId) => api.get(`/sessions/students/${studentId}/feeding-questionnaire/`),
  updateStudentFeedingQuestionnaire: (studentId, data) => api.patch(`/sessions/students/${studentId}/feeding-questionnaire/`, data),
  downloadParentPacketArchive: async (studentId) => {
    const res = await api.get(`/sessions/students/${studentId}/parent-packet/archive/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parent_packet_${studentId}_archive.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  // Medical profile (1:1, get_or_create)
  getMedicalProfile: (studentId) =>
    api.get(`/sessions/students/${studentId}/medical-profile/`),
  updateMedicalProfile: (studentId, data, config = {}) =>
    api.patch(`/sessions/students/${studentId}/medical-profile/`, data, config),

  // Allergies (up to 5 rows)
  getAllergies: (studentId) =>
    api.get(`/sessions/students/${studentId}/allergies/`),
  createAllergy: (studentId, data, config = {}) =>
    api.post(`/sessions/students/${studentId}/allergies/`, data, config),
  updateAllergy: (studentId, allergyId, data, config = {}) =>
    api.patch(`/sessions/students/${studentId}/allergies/${allergyId}/`, data, config),
  deleteAllergy: (studentId, allergyId, config = {}) =>
    api.delete(`/sessions/students/${studentId}/allergies/${allergyId}/`, config),

  // Consent (1:1, get_or_create)
  getConsent: (studentId) =>
    api.get(`/sessions/students/${studentId}/consent/`),
  updateConsent: (studentId, data) =>
    api.patch(`/sessions/students/${studentId}/consent/`, data),

  // Pickup persons (up to 3 rows)
  getPickupPersons: (studentId) =>
    api.get(`/sessions/students/${studentId}/pickup-persons/`),
  createPickupPerson: (studentId, data) =>
    api.post(`/sessions/students/${studentId}/pickup-persons/`, data),
  updatePickupPerson: (studentId, pickupId, data) =>
    api.patch(`/sessions/students/${studentId}/pickup-persons/${pickupId}/`, data),
  deletePickupPerson: (studentId, pickupId) =>
    api.delete(`/sessions/students/${studentId}/pickup-persons/${pickupId}/`),
  uploadPhoto: (studentId, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/sessions/students/${studentId}/photo/`, formData);
  },
  deletePhoto: (studentId) => api.delete(`/sessions/students/${studentId}/photo/`),
  /** Authenticated photo bytes (img tags cannot send JWT). Caller revokes the object URL. */
  getPhotoObjectUrl: async (studentId) => {
    const res = await api.get(`/sessions/students/${studentId}/photo/`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },
  getPhotoBlob: async (studentId) => {
    const res = await api.get(`/sessions/students/${studentId}/photo/`, { responseType: 'blob' });
    return res.data;
  },
};

export const serviceMeetingAPI = {
  listMeetings: (studentId) => api.get(`/sessions/students/${studentId}/service-meetings/`),
  getMeeting: (studentId, meetingId) => api.get(`/sessions/students/${studentId}/service-meetings/${meetingId}/`),
  createMeeting: (studentId, data) => api.post(`/sessions/students/${studentId}/service-meetings/`, data),
  updateMeeting: (studentId, meetingId, data) => api.patch(`/sessions/students/${studentId}/service-meetings/${meetingId}/`, data),
  deleteMeeting: (studentId, meetingId) => api.delete(`/sessions/students/${studentId}/service-meetings/${meetingId}/`),
  getCommitteeTypes: (studentId) => api.get(`/sessions/students/${studentId}/service-meetings/committee-types/`),
  getActiveServices: (studentId, date) =>
    api.get(`/sessions/students/${studentId}/service-meetings/active-services/`, { params: date ? { date } : {} }),
  getCompliance: (studentId, year, month) =>
    api.get(`/sessions/students/${studentId}/service-meetings/compliance/`, { params: { year, month } }),
  assignTherapist: (studentId, serviceId, therapistId) =>
    api.patch(`/sessions/students/${studentId}/approved-services/${serviceId}/assign-therapist/`, { therapist: therapistId }),
  uploadDocument: (studentId, meetingId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/sessions/students/${studentId}/service-meetings/${meetingId}/upload-document/`, fd);
  },
  listSlots: (studentId) => api.get('/sessions/therapy/slots/', { params: { student: studentId } }),
  createSlot: (data) => api.post('/sessions/therapy/slots/', data),
  updateSlot: (slotId, data) => api.patch(`/sessions/therapy/slots/${slotId}/`, data),
};

/** @deprecated use serviceMeetingAPI */
export const iepAPI = serviceMeetingAPI;

export const classroomAPI = {
  list: (sessionId) => api.get('/sessions/classrooms/', { params: { session: sessionId } }),
  get: (id) => api.get(`/sessions/classrooms/${id}/`),
  create: (data) => api.post('/sessions/classrooms/', data),
  update: (id, data) => api.patch(`/sessions/classrooms/${id}/`, data),
  delete: (id) => api.delete(`/sessions/classrooms/${id}/`),
  enroll: (classroomId, studentId) => api.post(`/sessions/classrooms/${classroomId}/enroll/`, { student: studentId }),
  enrollCheck: (classroomId, studentId) =>
    api.get(`/sessions/classrooms/${classroomId}/enroll-check/`, { params: { student: studentId } }),
  remove: (classroomId, studentId) => api.post(`/sessions/classrooms/${classroomId}/remove/${studentId}/`),
  unassigned: (sessionId) => api.get('/sessions/classrooms/unassigned/', { params: { session: sessionId } }),
  listWaivers: (classroomId) => api.get(`/sessions/classrooms/${classroomId}/waivers/`),
  createWaiver: (classroomId, data) => api.post(`/sessions/classrooms/${classroomId}/waivers/`, data),
  updateWaiver: (classroomId, waiverId, data) =>
    api.patch(`/sessions/classrooms/${classroomId}/waivers/${waiverId}/`, data),
  deleteWaiver: (classroomId, waiverId) => api.delete(`/sessions/classrooms/${classroomId}/waivers/${waiverId}/`),
  waiverReport: (sessionId) => api.get('/sessions/classrooms/waiver-report/', { params: { session: sessionId } }),
  waiverRenewalAlerts: (sessionId) =>
    api.get('/sessions/classrooms/waiver-renewal-alerts/', { params: { session: sessionId } }),
  listApprovals: (classroomId) => api.get(`/sessions/classrooms/${classroomId}/sedcar-approvals/`),
  createApproval: (classroomId, data) => {
    if (data instanceof FormData) {
      return api.post(`/sessions/classrooms/${classroomId}/sedcar-approvals/`, data);
    }
    return api.post(`/sessions/classrooms/${classroomId}/sedcar-approvals/`, data);
  },
  deleteApproval: (classroomId, approvalId) =>
    api.delete(`/sessions/classrooms/${classroomId}/sedcar-approvals/${approvalId}/`),
};

/** Narrow Gan Ezra roster self-service (move/add only). */
export const rosterAPI = {
  classrooms: (sessionId) => api.get('/sessions/roster/classrooms/', { params: { session: sessionId } }),
  students: (classroomId) => api.get(`/sessions/roster/classrooms/${classroomId}/students/`),
  unassigned: (sessionId) => api.get('/sessions/roster/unassigned/', { params: { session: sessionId } }),
  assign: (data) => api.post('/sessions/roster/assign/', data),
  pending: (sessionId) => api.get('/sessions/roster/pending/', { params: { session: sessionId } }),
  cancelPending: (id) => api.post(`/sessions/roster/pending/${id}/cancel/`),
};

export const therapyAPI = {
  listSessions: (params) => api.get('/sessions/therapy/sessions/', { params }),
  listAllSlots: (params) => api.get('/sessions/therapy/slots/', { params }),
  updateSlot: (slotId, data) => api.patch(`/sessions/therapy/slots/${slotId}/`, data),
  getTherapistSchedule: (therapistId, date) =>
    api.get('/sessions/therapy/therapist-schedule/', { params: { therapist: therapistId, date } }),
  markMissed: (sessionId, data) => api.post(`/sessions/therapy/sessions/${sessionId}/mark-missed/`, data),
  completeSession: (sessionId, data) => api.post(`/sessions/therapy/sessions/${sessionId}/complete/`, data || {}),
  scheduleMakeup: (sessionId, data) => api.post(`/sessions/therapy/sessions/${sessionId}/schedule-makeup/`, data),
  getMakeupSlots: (missedSessionId) =>
    api.get('/sessions/therapy/makeup-slots/', { params: { missed_session: missedSessionId } }),
  getCalendarToken: () => api.get('/sessions/therapy/my-calendar-token/'),
  rotateCalendarToken: () => api.post('/sessions/therapy/my-calendar-token/rotate/'),
  listTherapists: (siteId) => api.get('/auth/users/', { params: { role: 'therapist', site: siteId } }),
};

// Parent packet — named exports for reliable use in StudentDetailView (avoids minification issues)
export const getParentPacketStatus = (studentId) => api.get(`/students/${studentId}/parent-packet/status/`);
export const sendParentPacket = (studentId) => api.post(`/students/${studentId}/parent-packet/send/`);
export const setParentPacketReviewed = (studentId, reviewed) =>
  api.post(`/students/${studentId}/parent-packet/reviewed/`, { reviewed: !!reviewed });
export const setParentPacketPaperComplete = (studentId, payload) =>
  api.post(`/students/${studentId}/parent-packet/paper-complete/`, payload);
export const remindParentPacket = (studentId) => api.post(`/students/${studentId}/parent-packet/remind/`);
export const resetParentPacket = (studentId) => api.post(`/students/${studentId}/parent-packet/reset/`);
// Packet recipients report + pre-send approval gate (Items 2a/2b, 2026-08-03)
export const getPacketRecipients = (sessionId, params = {}) =>
  api.get('/sessions/packet-recipients/', { params: { session: sessionId, ...params } });
export const getPacketRecipientsCsv = (sessionId, params = {}) =>
  api.get('/sessions/packet-recipients/', {
    params: { session: sessionId, export: 'csv', ...params },
    responseType: 'blob',
  });
export const setPacketSendApproval = (studentId, action) =>
  api.post(`/students/${studentId}/packet-approval/`, { action });

export const employeeAPI = {
  getEmployees: (sessionId) =>
    api.get('/sessions/employees/', {
      params: {
        ...(sessionId ? { session: sessionId } : {}),
        page_size: 2000,
      },
    }),
  getEmployee: (id) => api.get(`/sessions/employees/${id}/`),
  createEmployee: (data) => api.post('/sessions/employees/', sanitizeInput(data)),
  updateEmployee: (id, data) =>
    api.patch(`/sessions/employees/${id}/`, sanitizeInput(data)),
  deleteEmployee: (id) => api.delete(`/sessions/employees/${id}/`),
};

export const busInfoAPI = {
  get: (studentId) => api.get(`/sessions/students/${studentId}/bus-info/`),
  update: (studentId, data) => api.patch(`/sessions/students/${studentId}/bus-info/`, data),
};

export const transportationAPI = {
  list: (params) =>
    api.get('/sessions/transportation/', { params: { page_size: 2000, ...params } }),
  get: (id) => api.get(`/sessions/transportation/${id}/`),
  create: (data) => api.post('/sessions/transportation/', data),
  update: (id, data) => api.patch(`/sessions/transportation/${id}/`, data),
  delete: (id) => api.delete(`/sessions/transportation/${id}/`),
  prefill: (studentId) =>
    api.get('/sessions/transportation/prefill/', { params: { student_id: studentId } }),
  sendToParent: (id) => api.post(`/sessions/transportation/${id}/send-to-parent/`),
  downloadPdf: (id) =>
    api.get(`/sessions/transportation/${id}/pdf/`, { responseType: 'blob' }),
  downloadCountyTrf: (id) =>
    api.get(`/sessions/transportation/${id}/county-trf/`, { responseType: 'blob' }),
};

export const passwordResetAPI = {
  requestReset: (email) => api.post('/auth/password-reset/', { email }),
  confirmReset: (uid, token, newPassword) =>
    api.post('/auth/password-reset/confirm/', { uid, token, new_password: newPassword }),
};

export const mfaAPI = {
  getStatus: () => api.get('/auth/mfa/setup/'),
  setup: () => api.post('/auth/mfa/setup/'),
  verify: (token, deviceId) =>
    api.post('/auth/mfa/verify/', { token, ...(deviceId ? { device_id: deviceId } : {}) }),
  disable: () => api.delete('/auth/mfa/setup/'),
};

export const userManagementAPI = {
  getUsers: (params) => api.get('/auth/users/', { params }),
  getUser: (id) => api.get(`/auth/users/${id}/`),
  createUser: (data) => api.post('/auth/users/', data),
  updateUser: (id, data) => api.patch(`/auth/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}/`),
  getSites: () => api.get('/sessions/sites/'),
};

export const complianceAPI = {
  getAccessLogs: (params) => api.get('/compliance/access-logs/', { params }),
  getDisclosures: (params) => api.get('/compliance/disclosures/', { params }),
  getSecurityEvents: (params) =>
    api.get('/compliance/security-events/', { params }),
  getAccessReport: (params) =>
    api.get('/compliance/reports/access/', { params }),
  getDisclosureReport: (params) =>
    api.get('/compliance/reports/disclosures/', { params }),
};

export const formsAPI = {
  getTemplates: async () => {
    const res = await api.get('/forms/templates/');
    const data = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
    return { ...res, data };
  },
  getTemplate: (formId) => api.get(`/forms/templates/${encodeURIComponent(formId)}/`),
  updateTemplate: (formId, data) => api.patch(`/forms/templates/${encodeURIComponent(formId)}/`, data),
  getTemplateVersions: (formId) => api.get(`/forms/templates/${encodeURIComponent(formId)}/versions/`),
  getTemplateVersion: (formId, versionId) =>
    api.get(`/forms/templates/${encodeURIComponent(formId)}/versions/${versionId}/`),
  revertTemplateToVersion: (formId, versionId) =>
    api.patch(`/forms/templates/${encodeURIComponent(formId)}/`, { revert_to_version_id: versionId }),
  previewForm: (params) => api.get('/forms/preview/', { params, responseType: 'blob' }),
  // "Current copy": live render including staff edits, watermarked server-side.
  // formId may be a real form id or the alias 'emergency' (resolved per site).
  printCurrentCopy: async (studentId, formId) => {
    const res = await api.get('/forms/current-copy/', {
      params: { student_id: studentId, form_id: formId },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formId}_current_copy_${studentId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

/** Payroll Allocation (PCS) — finance / superadmin only */
export const payrollAPI = {
  listCompanyCodes: () => api.get('/payroll/company-codes/'),
  listCostCenterCodes: () => api.get('/payroll/cost-center-codes/'),
  listCFRCodes: () => api.get('/payroll/cfr-codes/'),
  listProgramCodes: () => api.get('/payroll/program-codes/'),
  listCheckTypeCodes: () => api.get('/payroll/check-type-codes/'),
  updateCompanyCode: (id, data) => api.patch(`/payroll/company-codes/${id}/`, data),
  listEmployees: (params) => api.get('/payroll/employees/', { params }),
  getEmployee: (id) => api.get(`/payroll/employees/${id}/`),
  createEmployee: (data) => api.post('/payroll/employees/', data),
  updateEmployee: (id, data) => api.patch(`/payroll/employees/${id}/`, data),
  deleteEmployee: (id) => api.delete(`/payroll/employees/${id}/`),
  listAllocationSets: (params) => api.get('/payroll/allocation-sets/', { params }),
  getAllocationSet: (id) => api.get(`/payroll/allocation-sets/${id}/`),
  createAllocationSet: (data) => api.post('/payroll/allocation-sets/', data),
  updateAllocationSet: (id, data) => api.patch(`/payroll/allocation-sets/${id}/`, data),
  deleteAllocationSet: (id) => api.delete(`/payroll/allocation-sets/${id}/`),
  listAllocationLines: (params) => api.get('/payroll/allocation-lines/', { params }),
  createAllocationLine: (data) => api.post('/payroll/allocation-lines/', data),
  updateAllocationLine: (id, data) => api.patch(`/payroll/allocation-lines/${id}/`, data),
  deleteAllocationLine: (id) => api.delete(`/payroll/allocation-lines/${id}/`),
};

export default api;
